import React, { Fragment, useEffect, useState } from 'react';
import { CardBody, Row, Table } from 'reactstrap';

import {
  Column,
  Table as ReactTable,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

import { useSelector } from 'react-redux';
import { RootState } from 'slices';

import {
  EntriesPagination,
  ResultsPagination,
} from 'Components/pos2u/Pagination';

import TextSelectedItems from 'Components/pos2u/TextSelectedItems';
import CategorySearchForm from 'pages/Category/CategorySearchForm';
import { CreateCategory } from 'types/category/categoryTypes';
import { useTranslation } from 'react-i18next';
import useWindowSize from 'Components/Hooks/WindowSizeHook';
import Loader from 'Components/Common/Loader';

// Column Filter
const Filter = ({
  column,
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        // value={(columnFilterValue ?? '') as string}
        value={typeof columnFilterValue === 'string' ? columnFilterValue : ''}
        onChange={(value) => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      id="search-bar-0"
      className="form-control search"
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

interface TableContainerProps {
  columns: ColumnDef<any, any>[];
  data?: any;
  isCategoryQuery?: boolean;
  customPageSize: number;
  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  thClass?: string;
  divClass?: string;
  selectedCheckBoxDelete?: number;
  TotalData: number;
  totalPages: number;
  currentPage: number;
  setEntriesPerPage: (value: number) => void;
  handlePageChange: (value: number) => void;
  handleSearchFiltersSubmit?: (value: any) => void;
  initialFilters?: any;
  inputPageValue: number | null;
  setInputPageValue: (value: number | null) => void;
  handleCreateCategory: () => void;
  isAddingCategory: boolean;
  newCategory: CreateCategory;
  setNewCategory: (value: CreateCategory) => void;
  handleCancelCreateCategory: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  isTableLoadSuccess: boolean;
}

const CategoryTable = ({
  columns,
  data,
  isCategoryQuery = false,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  selectedCheckBoxDelete,
  TotalData,
  totalPages,
  currentPage,
  setEntriesPerPage,
  handlePageChange,
  handleSearchFiltersSubmit,
  initialFilters,
  inputPageValue,
  setInputPageValue,
  handleCreateCategory,
  isAddingCategory,
  newCategory,
  setNewCategory,
  handleCancelCreateCategory,
  inputRef,
  isTableLoadSuccess,
}: TableContainerProps) => {
  const layoutState = useSelector((state: RootState) => state.Layout);
  const { layoutModeType } = layoutState;
  const isDarkMode = layoutModeType === 'dark';
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns: columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { getHeaderGroups, getRowModel, setPageSize, getState } = table;

  useEffect(() => {
    if (Number(customPageSize)) {
      setPageSize(Number(customPageSize));
    }
  }, [customPageSize, setPageSize]);

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      handlePageChange(currentPage - 1);
    }
    setInputPageValue(currentPage);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      handlePageChange(currentPage + 1);
      setInputPageValue(currentPage + 2);
    }
  };

  const [isFormError, setIsFormError] = useState(false);

  return (
    <Fragment>
      {isCategoryQuery && initialFilters && handleSearchFiltersSubmit && (
        <Row className="mb-3">
          <CardBody className="border border-dashed border-end-0 border-start-0">
            <CategorySearchForm
              _isDarkMode={isDarkMode}
              onSubmit={handleSearchFiltersSubmit}
            />
            {selectedCheckBoxDelete && selectedCheckBoxDelete > 0 ? (
              <TextSelectedItems selectedItems={selectedCheckBoxDelete} />
            ) : null}
          </CardBody>
        </Row>
      )}

      {!isMobile && (
        <EntriesPagination
          customPageSize={customPageSize}
          setEntriesPerPage={setEntriesPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          inputPageValue={inputPageValue}
          setInputPageValue={setInputPageValue}
        />
      )}

      <div className={divClass}>
        <Table
          hover
          className={tableClass}
          style={{
            width: '100%',
          }}
        >
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => (
                  <th
                    key={header.id}
                    className={thClass}
                    style={{
                      ...header.column.columnDef.meta?.style,
                    }}
                    {...{
                      onClick: header.column.getToggleSortingHandler(),
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getIsSorted() === 'asc'
                          ? ' '
                          : header.column.getIsSorted() === 'desc'
                          ? ' '
                          : null}
                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {isTableLoadSuccess ? (
            <tbody>
              {isAddingCategory && (
                <tr
                  style={{
                    backgroundColor: isDarkMode ? '#424242' : '#ECEFF1',
                  }}
                  // onMouseLeave={() =>
                  //   setIsAddingCategory && setIsAddingCategory(false)
                  // }
                >
                  <td></td>
                  <td>
                    <input
                      ref={inputRef}
                      name="categoryName"
                      id="create-categoryName"
                      type="text"
                      placeholder="Category Name"
                      className="form-control"
                      value={newCategory.name}
                      onChange={(e) => {
                        setIsFormError(e.target.value === '');

                        setNewCategory({
                          ...newCategory,
                          name: e.target.value,
                        });
                      }}
                      onBlur={(e) => {
                        setIsFormError(e.target.value === '');
                      }}
                      style={{ width: '80%' }}
                    />
                    {isFormError && (
                      <div className="text-danger py-1">
                        {' '}
                        {t('Category Name is required')}{' '}
                      </div>
                    )}
                  </td>
                  <td style={{ alignContent: 'flex-start' }}>
                    <input
                      id="create-categorySortOrder"
                      name="sortOrder"
                      type="number"
                      placeholder="Sort Order"
                      className="form-control"
                      value={newCategory.sort_order}
                      onChange={(e) =>
                        setNewCategory({
                          ...newCategory,
                          sort_order: parseInt(e.target.value, 10),
                        })
                      }
                      style={{ width: '80%' }}
                    />
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <button
                        // className="btn btn-success btn-xl"
                        className="btn w-sm btn-primary material-shadow-none"
                        onClick={handleCreateCategory}
                        disabled={isFormError}
                      >
                        {t('Confirm')}
                      </button>
                      <button
                        // className="btn btn-danger btn-xl"
                        className="btn w-sm btn-light material-shadow-none"
                        onClick={handleCancelCreateCategory} // Hide form
                      >
                        {t('Btn_Cancel')}
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={getHeaderGroups()[0].headers.length}>
                    <div className="text-muted text-center">
                      <span>{t('No results found')}</span>
                    </div>
                  </td>
                </tr>
              ) : (
                getRowModel().rows.map((row: any) => {
                  return (
                    <tr
                      key={row.id}
                      className={`row-${row.original.category_number}`}
                    >
                      {row.getVisibleCells().map((cell: any) => {
                        return (
                          <td
                            key={cell.id}
                            style={{
                              ...cell.column.columnDef.meta?.style,
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          ) : (
            <tr>
              <td colSpan={getHeaderGroups()[0].headers.length}>
                <div className="d-flex justify-content-center align-items-center">
                  <Loader />
                </div>
              </td>
            </tr>
          )}
        </Table>
      </div>

      <ResultsPagination
        showingData={getState().pagination.pageSize}
        TotalData={TotalData}
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        inputPageValue={inputPageValue}
        setInputPageValue={setInputPageValue}
      />
    </Fragment>
  );
};

export default CategoryTable;
